import { useEffect, useState, type ChangeEvent } from "react";
import { hasJoined } from "../../../../engine/selectors";
import type { Player, PlayerId, TournamentState } from "../../../../engine/types";
import type { PinMode } from "../../../../shared/pins";
import { Face } from "../../components/Face";
import { shrinkToJpeg } from "../../lib/photos";
import type { Tournament } from "../../lib/useTournament";
import { spinNickname } from "./nicknames";
import { PinEntry } from "./Pin";
import { lengthOf, useSent } from "./sent";

/** Where the join flow has got to: four round badges (name, PIN, picture, nickname), the current one filled. */
function Steps({ at }: { at: 1 | 2 | 3 | 4 }) {
  return (
    <ol className="join-steps" aria-label={`Step ${at} of 4`}>
      {[1, 2, 3, 4].map((n) => (
        <li key={n} className={n === at ? "now" : n < at ? "done" : ""}>
          {n}
        </li>
      ))}
    </ol>
  );
}

/**
 * Step 1: who are you. Real names, those nobody has joined as first, so a latecomer finds theirs
 * near the top and not under 25 others; then those already joined, with their faces. The PIN step
 * after it is where a name already taken is asked about (decision 39).
 */
export function WhoStep({ t, state, onChoose }: { t: Tournament; state: TournamentState; onChoose(id: PlayerId): void }) {
  const all = Object.values(state.players).sort((x, y) => x.name.localeCompare(y.name, "en"));
  const free = all.filter((p) => !hasJoined(p));
  const taken = all.filter(hasJoined);

  const tile = (p: Player, joined: boolean) => (
    <button key={p.id} type="button" className={"player-name" + (joined ? " taken" : "")} onClick={() => onChoose(p.id)}>
      {joined && <Face name={p.name} src={t.faceOf(p)} />}
      <span className="player-name-text">
        {p.name}
        {joined && <small>taken</small>}
      </span>
    </button>
  );

  return (
    <>
      <Steps at={1} />
      <h1>Who are you</h1>
      <p className="player-quiet">Tap your name.</p>
      {free.length > 0 && <div className="player-names">{free.map((p) => tile(p, false))}</div>}
      {taken.length > 0 && (
        <>
          {free.length > 0 && <h2 className="player-names-head">Already joined</h2>}
          <div className="player-names">{taken.map((p) => tile(p, true))}</div>
        </>
      )}
    </>
  );
}

/**
 * Step 2: the PIN (decision 39). A name with no PIN yet sets one, typed twice; a name with one
 * types it. Once it is right this phone is that player for this tournament and is not asked again.
 * Five wrong tries lock the name for five minutes, and the admins see it.
 */
export function PinStep({ t, player, onBack }: { t: Tournament; player: Player; onBack(): void }) {
  // what the server says about this name: null while asking
  const [step, setStep] = useState<{ mode: PinMode; lockedUntil: number | null } | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const status = t.status;

  useEffect(() => {
    if (status !== "open" || step) return;
    let gone = false;
    void t.pinCheck(player.id).then((a) => {
      if (gone) return;
      if (!a.ok) setProblem(a.reason ?? "That did not work. Try again.");
      else setStep({ mode: a.set ? "enter" : "set", lockedUntil: a.lockedUntil ?? null });
    });
    return () => {
      gone = true;
    };
  }, [player.id, status, step === null]);

  const joined = hasJoined(player);
  const words =
    step?.mode === "enter"
      ? `Hello ${player.name}. Type the PIN you set for your name.`
      : joined
        ? `Somebody has already joined as ${player.name} without a PIN. If that was you, set one now: four numbers only you know.`
        : `Hello ${player.name}. Pick four numbers only you know, so nobody else can be you. This phone remembers it.`;

  return (
    <>
      <Steps at={2} />
      {/* nothing about the name until the server has said whether it has a PIN */}
      <h1>{!step ? "Your PIN" : step.mode === "enter" ? "Type your PIN" : "Set a PIN"}</h1>
      <div className="pin-who">
        <Face name={player.name} src={t.faceOf(player)} />
        <p>{step ? words : problem ?? "Asking about your name"}</p>
      </div>
      {step && (
        <PinEntry
          key={step.mode}
          mode={step.mode}
          yes={step.mode === "set" ? "Set my PIN" : "That is my PIN"}
          lockedUntil={step.lockedUntil}
          send={(pin) => t.claim(player.id, pin, step.mode)}
          // someone else set it a moment ago, or an admin reset it: change step
          onAnswer={(a) => {
            if (!a.ok && a.set !== undefined && (a.set ? "enter" : "set") !== step.mode) setStep({ mode: a.set ? "enter" : "set", lockedUntil: a.lockedUntil ?? null });
          }}
        />
      )}
      {step && (
        <p className="player-quiet">
          {step.mode === "enter" ? "Someone else put a PIN on your name? Ask an admin to reset it." : "An admin can reset it if you forget it."}
        </p>
      )}
      <button type="button" className="player-link" onClick={onBack}>
        Not {player.name}? Go back
      </button>
    </>
  );
}

/**
 * The camera: a big button that opens the phone's own camera, then a round preview with "Use
 * this" and "Retake". Used by the join flow and by the profile sheet.
 */
export function PictureTaker({
  t,
  player,
  onStored,
}: {
  t: Tournament;
  player: Player;
  onStored(): void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  // what went wrong: the server or the network refused it (try again), or the phone cannot read it
  const [problem, setProblem] = useState<string | null>(null);
  const [unreadable, setUnreadable] = useState(false);
  // A picture this phone cannot read is dropped at once, so Choose a photo is back.
  const cannotRead = (): void => {
    setFile(null);
    setProblem(null);
    setUnreadable(true);
  };

  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const pick = (e: ChangeEvent<HTMLInputElement>): void => {
    const picked = e.target.files?.[0] ?? null;
    // so the same picture can be picked again after a retake
    e.target.value = "";
    if (!picked) return;
    setProblem(null);
    setUnreadable(false);
    setFile(picked);
  };

  const use = async (): Promise<void> => {
    if (!file || sending) return;
    setSending(true);
    setProblem(null);
    let jpeg: Blob;
    try {
      jpeg = await shrinkToJpeg(file);
    } catch {
      setSending(false);
      cannotRead();
      return;
    }
    let why: string | null;
    try {
      why = await t.uploadPhoto(player.id, jpeg);
    } catch {
      why = "No connection. Try again in a moment.";
    }
    setSending(false);
    if (why) {
      setProblem(why);
      return;
    }
    setFile(null);
    onStored();
  };

  const camera = (label: string, kind: string) => (
    <label className={`btn ${kind} join-camera` + (sending ? " disabled" : "")}>
      <input type="file" accept="image/*" capture="user" className="join-file" onChange={pick} disabled={sending} />
      {label}
    </label>
  );
  // A picture already on the phone (decision 43): for a camera that is refused, or a browser
  // (WhatsApp's) that will not open it, so fewer people need an admin to wave them through.
  const gallery = (label: string) => (
    <label className={"btn quiet join-camera" + (sending ? " disabled" : "")}>
      <input type="file" accept="image/*" className="join-gallery" onChange={pick} disabled={sending} />
      {label}
    </label>
  );

  return (
    <div className="join-picture">
      {preview ? (
        <img className="face join-face" src={preview} alt="Your picture" onError={cannotRead} />
      ) : (
        <Face name={player.name} src={t.faceOf(player)} className="join-face" />
      )}
      {unreadable && (
        <p className="player-refused" role="alert">
          This phone cannot read that picture. Choose another, or take one.
        </p>
      )}
      {problem && (
        <p className="player-refused" role="alert">
          Not sent: {problem}
        </p>
      )}
      {file ? (
        <div className="join-actions">
          {camera("Retake", "quiet")}
          <button type="button" className="btn accent" disabled={sending} onClick={() => void use()}>
            {sending ? "Sending" : problem ? "Try again" : "Use this"}
          </button>
        </div>
      ) : (
        <div className="join-choose">
          {camera(player.photo > 0 ? "Retake the picture" : "Open the camera", "accent")}
          {gallery("Choose a photo")}
        </div>
      )}
    </div>
  );
}

/** Step 3: the picture. It cannot be skipped; an admin can wave someone through. */
export function PictureStep({ t, player, onStored, onNotMe }: { t: Tournament; player: Player; onStored(): void; onNotMe(): void }) {
  return (
    <>
      <Steps at={3} />
      <h1>Take a picture</h1>
      <p className="player-quiet">Hello {player.name}. Your face goes on the wall beside your name.</p>
      <PictureTaker t={t} player={player} onStored={onStored} />
      <p className="player-quiet">If the camera will not work, choose a photo instead, or an admin can let you in without one.</p>
      <button type="button" className="player-link" onClick={onNotMe}>
        Not {player.name}?
      </button>
    </>
  );
}

/** A name as the screens compare them: trimmed, single spaces, any case. */
function nameKey(name: string): string {
  return name.trim().replace(/\s+/g, " ").toLowerCase();
}

/** Every nickname and new name in use (the names Spin keeps clear of), in nameKey form. */
function nicknamesTaken(state: TournamentState): Set<string> {
  const taken = new Set<string>();
  for (const p of Object.values(state.players)) {
    if (p.nickname) taken.add(nameKey(p.nickname));
    if (p.dub) taken.add(nameKey(p.dub.nickname));
  }
  return taken;
}

/**
 * True when someone else already goes by `value` on the wall: their nickname, the new name a
 * winner gave them, or their real name. My own names do not count.
 */
export function nameTaken(state: TournamentState, value: string, me: PlayerId): boolean {
  const key = nameKey(value);
  if (!key) return false;
  return Object.values(state.players).some(
    (p) => p.id !== me && (nameKey(p.name) === key || (p.nickname && nameKey(p.nickname) === key) || (p.dub && nameKey(p.dub.nickname) === key)),
  );
}

export const NAME_TAKEN = "Someone already goes by that. Try another.";

/** A nickname being typed: the field, the count, and a spin for a random one. */
export function NicknameField({
  state,
  me,
  value,
  onChange,
  label,
}: {
  state: TournamentState;
  me: PlayerId;
  value: string;
  onChange(next: string): void;
  label: string;
}) {
  const max = state.config.bench.nicknameMax;
  const spin = (): void => {
    onChange(spinNickname(nicknamesTaken(state), value, max));
  };
  const taken = nameTaken(state, value, me);
  return (
    <div className="join-nick">
      <label className="join-field">
        <span>{label}</span>
        {/* no maxLength: it counts an emoji as two, the count and the server count it as one */}
        <input
          type="text"
          value={value}
          autoComplete="off"
          autoCapitalize="words"
          spellCheck={false}
          enterKeyHint="done"
          onChange={(e) => onChange([...e.target.value].slice(0, max).join(""))}
        />
      </label>
      <span className="join-count">
        {[...value].length}/{max}
      </span>
      <button type="button" className="btn quiet join-spin" onClick={spin}>
        Spin
      </button>
      {taken && (
        <p className="player-refused join-taken" role="alert">
          {NAME_TAKEN}
        </p>
      )}
    </div>
  );
}

/** Step 4: a nickname, or not. */
export function NicknameStep({ t, state, player, onDone }: { t: Tournament; state: TournamentState; player: Player; onDone(): void }) {
  const [value, setValue] = useState(player.nickname ?? "");
  const sent = useSent(t);
  useEffect(() => {
    if (sent.landed) onDone();
  }, [sent.landed, onDone]);
  return (
    <>
      <Steps at={4} />
      <h1>Pick a nickname</h1>
      <div className="join-in">
        <Face name={player.name} src={t.faceOf(player)} />
        <p>You are in, {player.name}. A nickname goes on the wall instead of your name. You can leave it.</p>
      </div>
      <NicknameField state={state} me={player.id} value={value} onChange={setValue} label="Nickname" />
      {sent.refused && (
        <p className="player-refused" role="alert">
          Not saved: {sent.refused}
        </p>
      )}
      <div className="join-actions">
        <button type="button" className="btn quiet" onClick={onDone}>
          Skip
        </button>
        <button
          type="button"
          className="btn accent"
          disabled={!lengthOf(value) || sent.waiting || nameTaken(state, value, player.id)}
          onClick={() => sent.send({ type: "player.profile", id: player.id, nickname: value })}
        >
          {sent.waiting ? "Saving" : "Done"}
        </button>
      </div>
    </>
  );
}
